import type { Address } from '@/lib/services/addresses/addresses.types'
import type { DeliveryAttempt } from '@/lib/services/deliveries/deliveries.types'
import type {
  DeliveryRouteStop,
  DeliveryRouteStopAddressInput,
  DeliveryRouteStopInput,
  DeliveryRouteStopSource,
  DeliveryShare,
  DeliveryShareLiveState,
  DeliveryShareLiveStateStop,
} from '@/lib/services/delivery-shares/delivery-shares.types'
import type { LocationListItem } from '@/lib/services/locations/locations.types'
import type {
  SalesOrderFulfillmentStatus,
  SalesOrderItem,
} from '@/lib/services/sales-orders/sales-orders.types'

import { DELIVERY_ROUTE_STOP_LIMIT } from './delivery-route-constants'
import { type DeliveryRouteSelectionId, getDeliveryRouteServerStops } from './delivery-share.utils'

/** A manual stop's address: saved on the route, or entered in the manual delivery dialog. */
type ManualDeliveryAddress = Address | DeliveryRouteStopAddressInput

/**
 * A manual stop the route pages edit: a saved `DeliveryRouteStop`, a stop shown in an order's
 * place, or a draft from the manual delivery dialog, which has no route fields yet.
 */
interface ManualDeliveryStopFields {
  id?: number | null
  route_stop_id?: number | null
  selection_id?: DeliveryRouteSelectionId | null
  reference_id?: string | null
  recipient_name?: string | null
  recipient_phone?: string | null
  recipient_email?: string | null
  /** `YYYY-MM-DD`; an explicit null clears the stop's date. */
  delivery_date?: string | null
  fulfillment_due_date?: string | null
  fulfillment_status?: SalesOrderFulfillmentStatus | null
  fulfillment_type?: DeliveryRouteStop['fulfillment_type']
  special_instructions?: string | null
  delivery_address?: ManualDeliveryAddress | null
  sales_order_items?: SalesOrderItem[]
  delivery_attempt_count?: number
  delivery_attempts?: DeliveryAttempt[]
}

/** The route a manual stop is shown on; the create page passes its organization. */
interface ManualDeliveryStopRoute {
  organization_id?: number
  location?: LocationListItem | null
}

/** The order and route-stop fields a manual stop drops when it is shown in an order's place. */
type ManualDeliveryDisplayStopDroppedKey =
  | 'id'
  | 'route_stop_id'
  | 'selection_id'
  | 'reference_id'
  | 'fulfillment_status'
  | 'fulfillment_type'
  | 'sales_order_items'

/** A manual stop shown in an order's place on the route pages and maps. */
export type ManualDeliveryDisplayStop<Stop> = Omit<Stop, ManualDeliveryDisplayStopDroppedKey> & {
  selection_id: DeliveryRouteSelectionId
  route_stop_id: number | null
  source_type: 'manual'
  organization_id: number | undefined
  location_id: number | null
  location: LocationListItem | null
  fulfillment_due_date: string | null | undefined
  delivery_attempt_count: number
  delivery_attempts: DeliveryAttempt[]
}

/** An order-backed route stop shown in its order's place, keyed by the sales order id. */
export interface SalesOrderRouteDisplayStop extends Omit<DeliveryRouteStop, 'id' | 'source_type'> {
  /** The sales order id. */
  id: number | null
  selection_id: number | null
  route_stop_id: number
  source_type: 'sales_order'
}

export type DeliveryRouteDisplayStop =
  | SalesOrderRouteDisplayStop
  | ManualDeliveryDisplayStop<DeliveryRouteStop>

/** A stop to send back to the API: a route stop, a stop shown in its place, or a draft. */
interface DeliveryRouteStopPayloadSource extends ManualDeliveryStopFields {
  source_type?: DeliveryRouteStopSource
  sales_order_id?: number | null
}

/**
 * A stop as `buildDeliveryRouteStopPayload` sends it (`StoreDeliveryShareRequest`). The fields come
 * from saved stops and drafts as they are; the API validates them.
 */
export type DeliveryRouteStopPayload =
  | {
      id?: number
      source_type: 'manual'
      recipient_name: string | null | undefined
      recipient_phone: string | null
      recipient_email: string | null
      delivery_date: string | null | undefined
      special_instructions: string | null
      delivery_address: ManualDeliveryAddress | null | undefined
    }
  | { source_type: 'sales_order'; sales_order_id: number | null | undefined }

/** A route with the live progress the detail page polls merged in. */
export type DeliveryShareWithLiveState = DeliveryShare & {
  /** The live state's route version, once it has been merged. */
  live_route_version?: string
}

function getManualDeliveryDate(
  stop: Pick<ManualDeliveryStopFields, 'delivery_date' | 'fulfillment_due_date'>
): string | null | undefined {
  return Object.hasOwn(stop, 'delivery_date')
    ? stop.delivery_date
    : (stop.fulfillment_due_date ?? null)
}

export function canSaveManualDeliveryDraft({
  currentStopCount,
  isExistingDraft = false,
}: {
  currentStopCount: number
  isExistingDraft?: boolean
}): boolean {
  return isExistingDraft || currentStopCount < DELIVERY_ROUTE_STOP_LIMIT
}

export function getNextManualDeliveryDraftSelectionId(
  stops: readonly {
    selection_id?: DeliveryRouteSelectionId | null
    client_id?: DeliveryRouteSelectionId | null
  }[] = []
): string {
  const usedSelectionIds = new Set(
    stops
      .map((stop) => stop.selection_id ?? stop.client_id)
      .filter(Boolean)
      .map(String)
  )
  let sequence = 1

  while (usedSelectionIds.has(`manual:new:${sequence}`)) {
    sequence += 1
  }

  return `manual:new:${sequence}`
}

export function buildManualDeliveryDisplayStop<Stop extends ManualDeliveryStopFields>(
  stop: Stop,
  share: ManualDeliveryStopRoute | null | undefined,
  clientId: DeliveryRouteSelectionId | null = null
): ManualDeliveryDisplayStop<Stop> {
  const routeStopId = stop.id ?? stop.route_stop_id ?? null
  const selectionId = clientId ?? `manual:${routeStopId}`
  const {
    id: _id,
    route_stop_id: _routeStopId,
    selection_id: _selectionId,
    reference_id: _referenceId,
    fulfillment_status: _fulfillmentStatus,
    fulfillment_type: _fulfillmentType,
    sales_order_items: _salesOrderItems,
    ...stopFields
  } = stop

  return {
    ...stopFields,
    selection_id: selectionId,
    route_stop_id: routeStopId,
    source_type: 'manual',
    organization_id: share?.organization_id,
    location_id: share?.location?.id ?? null,
    location: share?.location ?? null,
    fulfillment_due_date: getManualDeliveryDate(stop),
    delivery_attempt_count: Number(stop.delivery_attempt_count || 0),
    delivery_attempts: stop.delivery_attempts || [],
  }
}

export function buildDeliveryRouteDisplayStops(
  share: (Pick<DeliveryShare, 'stops' | 'location'> & ManualDeliveryStopRoute) | null | undefined
): DeliveryRouteDisplayStop[] {
  const canonicalStops = share?.stops ?? []

  return canonicalStops.map((stop): DeliveryRouteDisplayStop => {
    if (stop.source_type === 'manual') {
      return buildManualDeliveryDisplayStop(stop, share)
    }

    const { id: routeStopId, ...stopFields } = stop

    return {
      ...stopFields,
      id: stop.sales_order_id,
      selection_id: stop.sales_order_id,
      route_stop_id: routeStopId,
      source_type: 'sales_order',
      sales_order_id: stop.sales_order_id,
      location: share?.location ?? stop.location,
    }
  })
}

export function buildDeliveryRouteStopPayload(
  stop: DeliveryRouteStopPayloadSource
): DeliveryRouteStopPayload {
  if (stop.source_type === 'manual') {
    const routeStopId = stop.route_stop_id ?? stop.id

    return {
      ...(routeStopId ? { id: routeStopId } : {}),
      source_type: 'manual',
      recipient_name: stop.recipient_name,
      recipient_phone: stop.recipient_phone ?? null,
      recipient_email: stop.recipient_email ?? null,
      delivery_date: getManualDeliveryDate(stop),
      special_instructions: stop.special_instructions ?? null,
      delivery_address: stop.delivery_address,
    }
  }

  return {
    source_type: 'sales_order',
    sales_order_id: stop.sales_order_id ?? stop.id,
  }
}

/**
 * A saved stop as the route update takes it back. The route saved its stops with these fields, so a stop missing one
 * can't be sent again: the caller refuses the update, as the API would.
 */
export function toDeliveryRouteStopInput(
  payload: DeliveryRouteStopPayload
): DeliveryRouteStopInput | null {
  if (payload.source_type === 'sales_order') {
    return payload.sales_order_id
      ? { source_type: 'sales_order', sales_order_id: payload.sales_order_id }
      : null
  }

  const address = payload.delivery_address
  if (
    !payload.recipient_name ||
    !address?.line1 ||
    !address.city ||
    !address.state ||
    !address.postal_code ||
    !address.country ||
    address.is_verified !== true
  ) {
    return null
  }

  return {
    ...(payload.id ? { id: payload.id } : {}),
    source_type: 'manual',
    recipient_name: payload.recipient_name,
    recipient_phone: payload.recipient_phone,
    recipient_email: payload.recipient_email,
    delivery_date: payload.delivery_date,
    special_instructions: payload.special_instructions,
    delivery_address: {
      line1: address.line1,
      line2: address.line2,
      city: address.city,
      state: address.state,
      postal_code: address.postal_code,
      postal_code_suffix: address.postal_code_suffix,
      country: address.country,
      timezone: address.timezone,
      lat: address.lat,
      lng: address.lng,
      is_verified: true,
      is_rooftop: address.is_rooftop,
      google_place_id: address.google_place_id,
    },
  }
}

export function getManualDeliveryDraftSignature(
  stops: readonly DeliveryRouteStopPayloadSource[]
): string {
  return JSON.stringify(
    stops.map((stop) => ({
      id: stop.route_stop_id ?? null,
      selection_id: stop.selection_id ?? null,
      payload: buildDeliveryRouteStopPayload(stop),
    }))
  )
}

export function mergeManualDeliveryDraftStops<
  Stop extends DeliveryRouteStopPayloadSource,
  Draft extends DeliveryRouteStopPayloadSource,
>(canonicalStops: readonly Stop[], manualDraftStops: readonly Draft[]): (Stop | Draft)[] {
  const draftsByRouteStopId = new Map(
    manualDraftStops
      .filter((stop) => stop.route_stop_id)
      .map((stop) => [Number(stop.route_stop_id), stop])
  )
  const existingRouteStopIds = new Set<number>()
  const currentStops = canonicalStops.map((stop) => {
    if (stop.source_type !== 'manual') {
      return stop
    }

    existingRouteStopIds.add(Number(stop.route_stop_id))
    const draft = draftsByRouteStopId.get(Number(stop.route_stop_id))

    if (!draft) {
      return stop
    }

    return {
      ...stop,
      recipient_name: draft.recipient_name,
      recipient_phone: draft.recipient_phone ?? null,
      recipient_email: draft.recipient_email ?? null,
      delivery_date: draft.delivery_date ?? null,
      fulfillment_due_date: draft.delivery_date ?? null,
      special_instructions: draft.special_instructions ?? null,
      delivery_address: draft.delivery_address,
    }
  })

  return [
    ...currentStops,
    ...manualDraftStops.filter(
      (stop) => !stop.route_stop_id || !existingRouteStopIds.has(Number(stop.route_stop_id))
    ),
  ]
}

export function removeUnsavedManualDeliveryDrafts<
  Stop extends Pick<ManualDeliveryStopFields, 'route_stop_id' | 'selection_id'>,
>(stops: readonly Stop[], stopIds: Iterable<DeliveryRouteSelectionId>): readonly Stop[] {
  const stopIdSet = new Set<DeliveryRouteSelectionId | null | undefined>(stopIds)

  if (stopIdSet.size === 0) {
    return stops
  }

  return stops.filter((stop) => stop.route_stop_id || !stopIdSet.has(stop.selection_id))
}

export function getDeliveryRouteEditSessionKey(
  share: Pick<DeliveryShare, 'id' | 'name' | 'location' | 'stops'> | null | undefined
): string {
  const serverStops = getDeliveryRouteServerStops(share)

  return JSON.stringify({
    id: share?.id ?? null,
    name: share?.name ?? null,
    location_id: share?.location?.id ?? null,
    stops: serverStops.map((stop) => ({
      id: stop.id,
      source_type: stop.source_type ?? 'sales_order',
      sales_order_id:
        stop.source_type === 'manual' ? null : (stop.sales_order_id ?? stop.id ?? null),
      sort_order: stop.sort_order,
      recipient_name: stop.source_type === 'manual' ? stop.recipient_name : null,
      recipient_phone: stop.source_type === 'manual' ? stop.recipient_phone : null,
      recipient_email: stop.source_type === 'manual' ? stop.recipient_email : null,
      delivery_date: stop.source_type === 'manual' ? stop.delivery_date : null,
      special_instructions: stop.source_type === 'manual' ? stop.special_instructions : null,
      delivery_address: stop.source_type === 'manual' ? stop.delivery_address : null,
    })),
  })
}

export function getDeliveryRouteReorderSessionKey(
  share: Pick<DeliveryShare, 'id' | 'status' | 'is_completed' | 'stops'> | null | undefined
): string {
  const serverStops = getDeliveryRouteServerStops(share)

  return JSON.stringify({
    id: share?.id ?? null,
    status: share?.status ?? null,
    is_completed: Boolean(share?.is_completed),
    stops: serverStops.map((stop) => ({
      id: stop.id,
      source_type: stop.source_type ?? 'sales_order',
      sales_order_id:
        stop.source_type === 'manual' ? null : (stop.sales_order_id ?? stop.id ?? null),
      sort_order: stop.sort_order,
      is_delivered: Boolean(stop.is_delivered),
    })),
  })
}

function mergeLiveStopState(
  stop: DeliveryRouteStop,
  liveStop: DeliveryShareLiveStateStop | undefined
): DeliveryRouteStop {
  if (!liveStop) {
    return stop
  }

  return {
    ...stop,
    sort_order: liveStop.sort_order,
    is_delivered: liveStop.is_delivered,
    is_route_missed: liveStop.is_route_missed,
    is_route_started: liveStop.is_route_started,
  }
}

export function mergeDeliveryRouteLiveState(
  share: DeliveryShare | undefined,
  liveState: DeliveryShareLiveState | undefined
): DeliveryShareWithLiveState | undefined {
  if (!share || !liveState || Number(liveState.id) !== Number(share.id)) {
    return share
  }

  const liveStopsById = new Map((liveState.stops ?? []).map((stop) => [Number(stop.id), stop]))
  const stops = (share.stops ?? [])
    .map((stop) => mergeLiveStopState(stop, liveStopsById.get(Number(stop.id))))
    .sort((left, right) => Number(left.sort_order) - Number(right.sort_order))

  return {
    ...share,
    status: liveState.status,
    is_completed: liveState.is_completed,
    completed_at: liveState.completed_at,
    started_at: liveState.started_at,
    stop_count: liveState.stop_count,
    delivered_count: liveState.delivered_count,
    missed_count: liveState.missed_count,
    remaining_count: liveState.remaining_count,
    driver_location: liveState.driver_location,
    live_route_version: liveState.route_version,
    ...(share.stops ? { stops } : {}),
  }
}
